/**
 * UI Components - Moon Moon Agency
 * Handles read more functionality, loader states, and UI components
 */

/*********************************************************************************************
 * 🚀 LOADER MODULE
 */
const LoaderManager = {
	// Local configuration
	config: {
		classes: {
			loading: 'mm-loading',
			active: 'mm-active',
			expanded: 'mm-expanded'
		},
		selectors: {
			readMoreToggle: '[data-read-more-toggle]',
			readMoreContainer: '[data-read-more]',
			readMoreText: '[data-read-more-text]',
			readMoreLabelShow: '[data-read-more-show]',
			readMoreLabelHide: '[data-read-more-hide]'
		}
	},

	/**
	 * Add loading state to specified element
	 */
	loadThis(elem) {
		if (!elem) {
			window.log('warn', 'LoaderManager', 'No element provided');
			return;
		}
		elem.classList.add(this.config.classes.loading);
		window.emit('global:loader-activated', { element: elem.tagName });
	}
};

/*********************************************************************************************
 * 📖 READ MORE MODULE
 */
const ReadMoreManager = {
	/**
	 * Toggle read more state
	 */
	toggle(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		const labelShow = btn.querySelector(LoaderManager.config.selectors.readMoreLabelShow);
		const labelHide = btn.querySelector(LoaderManager.config.selectors.readMoreLabelHide);

		if (!textElement || !labelShow || !labelHide) return;

		const expanded = btn.getAttribute('aria-expanded') === 'true';
		const newState = !expanded;

		// Update state
		textElement.classList.toggle(LoaderManager.config.classes.expanded, newState);
		btn.setAttribute('aria-expanded', newState.toString());
		labelShow.hidden = newState;
		labelHide.hidden = !newState;

		window.emit('global:read-more-toggled', { expanded: newState });
	},

	/**
	 * Initialize read more functionality
	 */
	init() {
		const toggleButtons = window.getElements(LoaderManager.config.selectors.readMoreToggle);
		if (!toggleButtons.length) return;

		toggleButtons.forEach((btn) => {
			// Initialize aria-expanded if not present
			if (!btn.hasAttribute('aria-expanded')) {
				btn.setAttribute('aria-expanded', 'false');
			}

			// Texte deja entierement visible (pas de troncature) : le bouton n'a pas lieu d'etre.
			// ResizeObserver plutot qu'un check ponctuel : se redeclenche a chaque changement de
			// taille de la boite (chargement de police, resize, franchissement d'un breakpoint
			// mobile) - un check unique au chargement peut mesurer avant que le layout final
			// (police reelle) ne soit en place et ne se corrige alors jamais.
			const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
			const textElement = container ? container.querySelector(LoaderManager.config.selectors.readMoreText) : null;
			if (textElement) {
				const syncVisibility = () => {
					// Ne jamais re-masquer un texte que l'utilisateur vient de deplier : une fois
					// etendu, max-height:none rend scrollHeight == clientHeight en permanence.
					if (textElement.classList.contains(LoaderManager.config.classes.expanded)) return;
					btn.hidden = textElement.scrollHeight <= textElement.clientHeight + 1;
				};

				if (window.ResizeObserver) {
					new ResizeObserver(syncVisibility).observe(textElement);
				} else {
					syncVisibility();
				}
			}

			// Click event
			btn.addEventListener('click', () => {
				this.toggle(btn);
			});

			// Keyboard support (Enter/Space)
			btn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(btn);
				}
			});
		});
	}
};

/*********************************************************************************************
 * 🔗 EXTERNAL LINKS MODULE
 */
const ExternalLinksManager = {
	/**
	 * Process external links to open in new tab
	 */
	init() {
		const links = window.getElements('a:not(.consent-wrapper)');
		let processedCount = 0;

		links.forEach((link) => {
			if (link.hostname !== window.location.hostname) {
				link.target = '_blank';
				link.rel = 'noreferrer noopener';
				processedCount++;
			}
		});

	}
};

/*********************************************************************************************
 * 📏 DOCUMENT HEIGHT MODULE
 */
const DocumentHeightManager = {
	/**
	 * Update document height CSS variable
	 */
	update() {
		const newHeight = `${window.innerHeight}px`;
		document.documentElement.style.setProperty('--doc-height', newHeight);
		window.emit('global:document-height-updated', { height: newHeight });
	}
};

/*********************************************************************************************
 * 🎠 SPLIDE MODULE
 */
const SplideManager = {
	_observer: null,

	_shouldDefer(element) {
		if (!window.IntersectionObserver) return false;
		const rect = element.getBoundingClientRect();
		if (rect.width && rect.top < window.innerHeight * 1.5 && rect.bottom > -window.innerHeight * 0.5) return false;
		if (!this._observer) {
			this._observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					this._observer.unobserve(entry.target);
					this.init([entry.target], true);
				});
			}, { rootMargin: '400px 0px' });
		}
		this._observer.observe(element);
		return true;
	},

	/**
	 * Initialize all Splide carousels with their data-splide configurations
	 */
	init(elements, force = false) {
		const splideElements = elements || window.getElements('.splide');
		if (!splideElements.length || typeof Splide === 'undefined') return;

		let initializedCount = 0;
		const sectionsWithSplide = [];

		// Skip already initialized sliders and product media sliders (synced and initialized in mm-product-medias.liquid)
		const pending = Array.prototype.filter.call(splideElements, (element) => {
			if (element._splide) return false;
			return !element.hasAttribute('data-main-slider') && !element.hasAttribute('data-thumbnail-slider');
		});
		const toMount = force ? pending : pending.filter((element) => !this._shouldDefer(element));

		toMount.forEach(element => {
			if (element._splide) return;

			try {
				const configData = element.dataset.splide;
				let splideInstance;

				if (configData) {
					try {
						const config = JSON.parse(configData);
						splideInstance = new Splide(element, config).mount();
					} catch (jsonError) {
						window.log('warn', 'SplideManager', `Invalid JSON in data-splide, using defaults: ${element.className}`);
						splideInstance = new Splide(element).mount();
					}
				} else {
					// Configuration par défaut si pas de data-splide
					splideInstance = new Splide(element).mount();
				}

				// Exposer l'instance sur l'element : active la garde anti-double-init
				// (ligne ~149) et permet un refresh() externe (ex: reveal d'un panneau masque).
				element._splide = splideInstance;

				// Gérer la progress bar si elle existe
				const progressBar = element.querySelector('.splide__progress__bar');
				if (progressBar && splideInstance) {
					// data-progress-style="thumb" (ex: mm-comparateur-matelas__progress) :
					// pouce de largeur proportionnelle au ratio visible, qui se translate en
					// suivant la position RÉELLE de la piste en pixels (écart entre les
					// rects de .splide__list et .splide__track) — même mécanisme, au pixel
					// près, que mm-pslider__progress (assets/mm-product-slider.js, basé sur
					// scrollLeft/scrollWidth), pas un simple remplissage 0→100% par palier
					// d'index. Sans ça, un slider en mode "slide" (transitions discrètes)
					// fait sauter le pouce par page entière au lieu de suivre le drag au doigt.
					// translateX(%) est TOUJOURS relatif à la largeur du POUCE lui-même
					// (jamais à celle du rail) : un déplacement pensé comme fraction du rail
					// doit donc être re-exprimé en fraction de la largeur du pouce.
					const rail = progressBar.closest('[data-progress-style="thumb"]');
					const updateProgress = function() {
						try {
							if (rail) {
								const { Elements } = splideInstance.Components;
								const listRect = Elements.list.getBoundingClientRect();
								const trackRect = Elements.track.getBoundingClientRect();
								// .splide__list est un flex container SANS largeur explicite : son
								// propre rect ne s'étire pas pour contenir ses enfants qui débordent
								// (comportement flexbox standard), il reste borné à la largeur du
								// track — scrollWidth, lui, mesure la vraie étendue du contenu.
								const listWidth = Elements.list.scrollWidth || listRect.width;
								const trackWidth = trackRect.width;
								const ratioVisible = listWidth > 0 ? Math.min(trackWidth / listWidth, 1) : 1;
								const maxPosition = Math.max(listWidth - trackWidth, 0);
								// Décalage réel de la piste, indépendant du sens LTR/RTL de Splide :
								// distance en pixels entre le bord gauche du track et celui de la
								// liste, bornée à [0, maxPosition] (même logique que
								// scrollLeft/scrollWidth côté mm-pslider__progress).
								const currentPosition = Math.min(Math.max(trackRect.left - listRect.left, 0), maxPosition);
								const ratioScroll = maxPosition > 0 ? currentPosition / maxPosition : 0;
								const thumbTravelPercent = ratioVisible > 0 ? (ratioScroll * (1 - ratioVisible) / ratioVisible * 100) : 0;
								progressBar.style.setProperty('--thumb-w', ratioVisible.toFixed(4));
								progressBar.style.setProperty('--thumb-x', thumbTravelPercent.toFixed(2) + '%');
								return;
							}

							const end = splideInstance.Components.Controller.getEnd() + 1;
							const rate = Math.min((splideInstance.index + 1) / end, 1);
							progressBar.style.width = String(100 * rate) + '%';
						} catch (error) {
							window.log('error', 'SplideManager', 'Progress bar update failed');
						}
					};

					// Initialiser immédiatement et au montage. 'drag dragging dragged' en plus
					// de 'move moved' : le pouce doit suivre le doigt PENDANT le geste, pas
					// seulement une fois la transition terminée (cf. rail ci-dessus).
					splideInstance.on('mounted ready', updateProgress);
					splideInstance.on(rail ? 'move moved drag dragging dragged scroll scrolled' : 'move moved', updateProgress);

					// Force l'initialisation après un court délai
					requestAnimationFrame(() => {
						setTimeout(updateProgress, 50);
					});
				}

				// Masquer les arrows si tous les slides sont visibles
				const arrowsEl = element.querySelector('.splide__arrows');
				if (arrowsEl) {
					const toggleArrows = () => {
						const overflow = splideInstance.Components.Layout.isOverflow();
						arrowsEl.style.visibility = overflow ? '' : 'hidden';
					};
					splideInstance.on('mounted resize', toggleArrows);
				}

				// Scroll horizontal trackpad / molette inclinable -> navigation slide
				this._bindWheelNav(splideInstance);

				// Fleches rendues hors du .splide (mm-slider-controls)
				this._bindExternalArrows(element, splideInstance);

				// Collecter les sections
				const sectionElement = element.closest('section');
				if (sectionElement) {
					const sectionName = sectionElement.className || 'unnamed-section';
					if (!sectionsWithSplide.includes(sectionName)) {
						sectionsWithSplide.push(sectionName);
					}
				}

				initializedCount++;

			} catch (error) {
				window.log('error', 'SplideManager', `Initialization failed for element: ${element.className}`);

				// Initialiser avec config par défaut en cas d'erreur
				try {
					element._splide = new Splide(element).mount();
					initializedCount++;
				} catch (fallbackError) {
					window.log('error', 'SplideManager', `Fallback initialization failed: ${fallbackError.message}`);
				}
			}
		});

		// Émettre un seul événement global avec la liste des sections
		if (initializedCount > 0) {
			window.emit('global:splide-initialized', {
				count: initializedCount,
				sections: sectionsWithSplide
			});
		}
	},

	/**
	 * Brancher les fleches du snippet mm-slider-controls posees HORS du `.splide`
	 * (Splide ne wire que celles presentes dans son root). Le relais generique
	 * emet 'mm:slider:prev' / 'mm:slider:next' sur l'element cible par
	 * data-slider-target : on ecoute et on deplace, puis on reflete l'etat
	 * disabled comme le fait la composante Arrows native.
	 *
	 * @param {HTMLElement} element - Root du slider (doit porter un id)
	 * @param {object} splideInstance - Instance Splide montee
	 */
	_bindExternalArrows(element, splideInstance) {
		if (!element.id) return;

		const scope = `[data-slider-target="${element.id}"]`;
		const prev = document.querySelector(`${scope}[data-slider-prev], ${scope} [data-slider-prev]`);
		const next = document.querySelector(`${scope}[data-slider-next], ${scope} [data-slider-next]`);
		if (!prev && !next) return;

		element.addEventListener('mm:slider:prev', () => splideInstance.go('<'));
		element.addEventListener('mm:slider:next', () => splideInstance.go('>'));

		const syncDisabled = () => {
			const { Controller } = splideInstance.Components;
			if (prev) prev.disabled = Controller.getPrev() < 0;
			if (next) next.disabled = Controller.getNext() < 0;
		};

		// 'ei' = endIndexChanged (recalcul du dernier index apres resize)
		splideInstance.on('mounted moved refresh scrolled updated ei', syncDisabled);
		syncDisabled();
	},

	/**
	 * Rendre un slider navigable au scroll horizontal (trackpad 2 doigts,
	 * molette inclinable, Shift+molette). Splide gere deja le drag souris/tactile,
	 * mais ignore le `wheel` horizontal : on le branche ici sur le track.
	 *
	 * @param {object} splideInstance - Instance Splide montee
	 */
	_bindWheelNav(splideInstance) {
		const track = splideInstance.Components.Elements.track;
		if (!track) return;

		// Throttle : le trackpad emet des dizaines d'events par geste.
		// On limite a un `go()` par intervalle pour ne pas sauter plusieurs slides.
		let lastMove = 0;
		const THROTTLE = 400;

		const onWheel = (e) => {
			// Intention horizontale uniquement : ne pas capturer le scroll vertical de la page
			if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

			// Slider sans overflow (tous les slides visibles) : laisser passer le scroll
			if (!splideInstance.Components.Layout.isOverflow()) return;

			// On agit horizontalement : empecher le geste back/forward du navigateur
			if (e.cancelable) e.preventDefault();

			const now = e.timeStamp;
			if (now - lastMove < THROTTLE) return;
			lastMove = now;

			splideInstance.go(e.deltaX > 0 ? '>' : '<');
		};

		// passive: false requis pour pouvoir preventDefault le geste horizontal
		track.addEventListener('wheel', onWheel, { passive: false });

		// Nettoyage si l'instance est detruite (re-render editeur de theme)
		splideInstance.on('destroy', () => {
			track.removeEventListener('wheel', onWheel, { passive: false });
		});
	}
};

/* MM / Dropdown (accordéon) — assets/mm-dropdown.js
   Anime l'ouverture/fermeture du <details> en douceur (hauteur réelle, Web
   Animations API). Ferme les autres items partageant le même attribut name
   (regroupement natif détourné pour pouvoir les animer aussi à la fermeture).
   Délégation d'événements : marche pour tout .mm-dropdown-item (et accordéon
   du pack funnel, .mm-pack-funnel-accordion) présent au clic, y compris ajouté
   après coup. Contrat : <details> > <summary> + un seul bloc de contenu. */
   (function () {
	'use strict';

	var ITEM = '.mm-dropdown-item, .mm-pack-funnel-accordion';
  
	function frameSize(details) {
	  /* Bordure (+ padding éventuel) du <details> lui-même : getBoundingClientRect()
		 sur le sommaire/la réponse ne mesure que leur propre boîte, pas la bordure
		 du parent — l'oublier fait "grandir" le panneau de quelques pixels une fois
		 l'animation terminée et la hauteur inline relâchée. */
	  var cs = window.getComputedStyle(details);
	  return parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth) +
		parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
	}
  
	/* Seuil d'amplitude au-delà duquel le rebond est retiré. Un overshoot de
	   56 % (curve-medium) déplace 8 px sur un contenu de 60, mais 90 px sur un
	   contenu de 900 : le contenu qui suit part et revient — c'est perçu comme un
	   glitch, pas comme du ressort (retour Killian du 02/08, accordéons détaillés
	   du product form). Au-delà du seuil on passe à curve-out, qui arrive à la
	   hauteur cible sans jamais la dépasser. */
	var REBOND_MAX = 320;
  
	function animateTo(details, endHeight, onDone, opening) {
	  var startHeight = details.getBoundingClientRect().height;
	  if (details._mmAnim) details._mmAnim.cancel();
	  details.style.overflow = 'hidden';
  
	  var amplitude = Math.abs(endHeight - startHeight);
	  /* Pli d'ENCART (note) : jamais de rebond. Sur un accordéon nu l'overshoot
		 ne déplace que du texte, mais ici il étire un FOND PLEIN au-delà de sa
		 hauteur d'arrivée (11 px mesurés sur 102 d'amplitude) avant de le
		 rétracter — la carte paraît se recadrer à la fin (retour Killian 05/08). */
	  var sansRebond = details.classList.contains('mm-note__fold');
	  /* ouverture : rebond léger tant que le déplacement reste petit, sinon
		 sortie franche ; fermeture : amortie (curve-soft), sans rebond */
	  var reglage = opening
		? (amplitude > REBOND_MAX
			? { duration: 560, easing: 'cubic-bezier(.22, 1, .36, 1)' }
			: sansRebond
			  ? { duration: 460, easing: 'cubic-bezier(.22, 1, .36, 1)' }
			  : { duration: 520, easing: 'cubic-bezier(.34, 1.56, .64, 1)' })
		: { duration: 420, easing: 'cubic-bezier(.26, 1.10, .40, 1)' };
  
	  var anim = details.animate(
		{ height: [startHeight + 'px', endHeight + 'px'] },
		reglage
	  );
	  details._mmAnim = anim;
	  anim.onfinish = function () {
		details._mmAnim = null;
		details._mmBusy = false;
		details.style.height = '';
		details.style.overflow = '';
		if (onDone) onDone();
	  };
	  anim.oncancel = function () {
		details._mmBusy = false;
		if (onDone) onDone(); /* relâche l'ancrage même quand l'animation est coupée */
		/* openDetails a posé une hauteur INLINE avant d'ouvrir : si l'animation
		   est annulée sans que personne ne prenne le relais, cette hauteur reste
		   figée et l'item paraît fermé alors que [open] est bien posé. On ne la
		   relâche que si aucune autre animation n'a démarré entre-temps — sinon
		   c'est elle qui pilote la hauteur. */
		if (details._mmAnim === anim) {
		  details._mmAnim = null;
		  details.style.height = '';
		  details.style.overflow = '';
		}
	  };
	}
  
	/* Piège Chromium : quand on enchaîne vite fermeture→réouverture, la valeur
	   de base d'une propriété en transition (opacité des icônes +/−, fond de la
	   pastille) fait 1→0→1 avant que le moteur ait digéré le changement ; la
	   transition « terminée » vers l'ancienne valeur reste en mémoire et
	   continue d'imposer son résultat — icône et pastille figées dans l'état
	   inverse de [open], définitivement (vu sur le pli note du product form,
	   03-04/08 ; ni reflow ni invalidation de classe ne suffisent). Purge
	   éprouvée : couper un instant les transitions de l'habit, forcer un
	   recalcul, les rendre — la valeur re-suit la cascade. Appelée une fois
	   l'état posé : les fondus (.18s) sont finis bien avant la hauteur
	   (420-560ms), rien de visible n'est coupé. */
	function resyncHabit(details) {
	  var nodes = [details];
	  var summary = details.querySelector(':scope > summary');
	  if (summary) nodes.push(summary);
	  var toggle = details.querySelector('.mm-dropdown-toggle, .mm-pack-funnel-accordion-chevron');
	  if (toggle) {
		nodes.push(toggle);
		Array.prototype.push.apply(nodes, toggle.children);
	  }
	  nodes.forEach(function (n) { n.style.transition = 'none'; });
	  void details.offsetWidth;
	  nodes.forEach(function (n) { n.style.transition = ''; });
	}
  
	function closeDetails(details) {
	  if (details._mmBusy || !details.open) return;
	  details._mmBusy = true;
	  /* is-closing retire tout de suite le look "ouvert" (icône, bordure) — le
		 [open] natif reste posé jusqu'à la fin pour que le contenu ne se coupe
		 pas net pendant que la hauteur se réduit. */
	  details.classList.add('is-closing');
	  details._mmClosePending = true;   /* intention : sert au filet anti-limbo */
	  var summary = details.querySelector(':scope > summary');
	  var endHeight = (summary ? summary.offsetHeight : 0) + frameSize(details);
	  animateTo(details, endHeight, function () {
		details._mmClosePending = false;
		details.open = false;
		details.classList.remove('is-closing');
		resyncHabit(details);
	  });
  
	  /* FILET : si l'animation de fermeture meurt sans onfinish (annulation par
		 une animation concurrente, onglet gelé, moteur capricieux…), l'item
		 resterait en limbo [open]+replié — l'icône « − » et l'habit « ouvert »
		 mentiraient (vu sur le pli de l'encart note, 03/08). Passé la durée de
		 l'animation + marge, si le limbo persiste, on pose l'état final à la
		 main. Idempotent : ne touche à rien si la fermeture s'est bien finie
		 ou si l'utilisateur a rouvert entre-temps. */
	  setTimeout(function () {
		if (details.open && details._mmClosePending) {
		  details._mmClosePending = false;
		  if (details._mmAnim) details._mmAnim.cancel();
		  details._mmAnim = null;
		  details._mmBusy = false;
		  details.open = false;
		  details.classList.remove('is-closing');
		  details.style.height = '';
		  details.style.overflow = '';
		  resyncHabit(details);
		}
	  }, 700);
	}
  
  /* Garde le sommaire cliqué IMMOBILE à l'écran pendant que les hauteurs
	   bougent. Sans ça, fermer un frère de 900 px au-dessus emporte la page :
	   on clique sur un titre, et il file hors de l'écran. Le navigateur a bien
	   un ancrage de scroll natif, mais il ne s'applique pas à une hauteur
	   animée en JS. La compensation s'arrête net dès que l'utilisateur scrolle
	   lui-même — sinon on lutterait contre lui. */
	function ancrer(summary) {
	  var ref = summary.getBoundingClientRect().top;
	  var actif = true;
	  var fin = Date.now() + 1200; /* filet : plus longue animation (560) + marge */
	  function stop() { actif = false; }
	  window.addEventListener('wheel', stop, { passive: true, once: true });
	  window.addEventListener('touchmove', stop, { passive: true, once: true });
	  (function tick() {
		/* la borne de temps est indispensable : si l'animation est ANNULÉE (clic
		   rapide sur un autre item), son onfinish ne relâche jamais l'ancrage —
		   sans elle, la boucle recollerait le scroll pour toujours et la page
		   paraîtrait bloquée. */
		if (!actif || Date.now() > fin) return;
		var ecart = summary.getBoundingClientRect().top - ref;
		if (Math.abs(ecart) > 0.5) window.scrollBy(0, ecart);
		window.requestAnimationFrame(tick);
	  })();
	  return stop;
	}
  
	/* L'événement natif 'toggle' est l'autorité finale de l'état d'un <details> :
	   quoi qu'il arrive aux animations, on y resynchronise les classes — un item
	   réellement fermé ne garde jamais un habit « ouvert » (et inversement). */
	document.addEventListener('toggle', function (e) {
	  var d = e.target;
	  if (!d.matches || !d.matches(ITEM)) return;
	  if (!d.open) d.classList.remove('is-closing');
	}, true);
  
	function openDetails(details) {
	  if (details._mmBusy || details.open) return;
	  details._mmBusy = true;
	  details._mmClosePending = false;   /* réouverture volontaire : le filet se tait */
  
	  var summaryClique = details.querySelector(':scope > summary');
	  var relacher = summaryClique ? ancrer(summaryClique) : null;
  
	  var name = details.getAttribute('name');
	  if (name) {
		document.querySelectorAll('details[name="' + name + '"]').forEach(function (sibling) {
		  if (sibling !== details) closeDetails(sibling);
		});
	  }
  
	  details.style.height = details.getBoundingClientRect().height + 'px';
	  details.open = true;
	  window.requestAnimationFrame(function () {
		var summary = details.querySelector(':scope > summary');
		var answer = details.querySelector(':scope > :not(summary)');
		/* offsetHeight, PAS getBoundingClientRect : la réponse porte un
		   transform d'entrée (translateY(-10px) scale(.99)) au moment de la
		   mesure, et getBoundingClientRect renvoie la hauteur TRANSFORMÉE —
		   99 % de la vraie. L'erreur est invisible sur 60px de texte, elle vaut
		   7px sur 700 et le <details>, qui clippe pendant l'animation, rognait
		   d'autant son dernier élément (retour Killian du 02/08 : le bouton
		   « Modifier la dimension » coupé). offsetHeight ignore les transforms. */
		var endHeight = (summary ? summary.offsetHeight : 0) +
		  (answer ? answer.offsetHeight + parseFloat(window.getComputedStyle(answer).marginTop) : 0) +
		  frameSize(details);
		animateTo(details, endHeight, function () {
		  if (relacher) relacher();
		  resyncHabit(details);
		}, true);
	  });
	}
  
	document.addEventListener('click', function (e) {
	  var summary = e.target.closest('summary');
	  var details = summary && summary.parentElement;
	  if (!details || !details.matches(ITEM)) return;
	  e.preventDefault();
  
	  if (details.open) closeDetails(details); else openDetails(details);
	});
  })();

/*********************************************************************************************
 * 🔗 SECTION LINK MANAGER
 */
const SectionLinkManager = {
	config: {
		selectors: {
			source: '[data-mm-link-source]',
			slot: '[data-mm-link-slot]',
		},
		classes: {
			hidden: 'mm-none',
		},
	},

	init() {
		window.getElements(this.config.selectors.source).forEach((source) => {
			const linkId = source.dataset.mmLinkSource;
			if (!linkId) return;

			const slot = document.querySelector(`${this.config.selectors.slot}[data-mm-link-slot="${CSS.escape(linkId)}"]`);
			if (slot && !source.contains(slot)) slot.appendChild(source);

			source.classList.remove(this.config.classes.hidden);
		});
	},
};

/*********************************************************************************************
 * 🎯 UI COMPONENTS MANAGER
 */
const UIComponentsManager = {
	/**
	 * Initialize all UI components
	 */
	init() {
		SectionLinkManager.init();
		ReadMoreManager.init();
		ExternalLinksManager.init();
		SplideManager.init();
		DocumentHeightManager.update();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		DocumentHeightManager.update();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.LoaderManager = LoaderManager;
window.ReadMoreManager = ReadMoreManager;
window.ExternalLinksManager = ExternalLinksManager;
window.SplideManager = SplideManager;
window.DocumentHeightManager = DocumentHeightManager;
window.SectionLinkManager = SectionLinkManager;
window.UIComponentsManager = UIComponentsManager;

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	UIComponentsManager.init();
});

// Re-init quand l'editeur de theme (re)charge une section : SplideManager est idempotent
// (garde `if (element._splide) return`), donc pas de double-mount des carousels existants.
document.addEventListener('shopify:section:load', () => UIComponentsManager.init());

window.addEventListener('resize', window.debounce(() => UIComponentsManager.onResize(), 150));
